import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Button variants, after the reference design: display face, 10 px radius,
 * flat surfaces, a brightness shift on hover and a 1 px press.
 *
 * - `primary`   the CTA — primary surface, primary foreground
 * - `ink`       dark secondary — ink surface, paper text (`secondary` is
 *               its older name and maps to the same look)
 * - `outline`   transparent with a hairline
 * - `ghost`     transparent, surface on hover
 * - `danger`    filled in the danger colour, for destructive confirms
 */
const buttonVariants = cva(
  // `dc-button` is the hook for embedding themes (styles/embed-polaris.css).
  'dc-button inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control font-display font-semibold tracking-[-0.01em] transition-[filter,background-color,color,transform] duration-[120ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-paper active:translate-y-px disabled:pointer-events-none disabled:opacity-40',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-fg ring-1 ring-inset ring-primary hover:brightness-[0.92]',
        ink: 'bg-ink text-paper ring-1 ring-inset ring-ink hover:brightness-[1.4]',
        secondary:
          'bg-ink text-paper ring-1 ring-inset ring-ink hover:brightness-[1.4]',
        outline:
          'bg-transparent text-ink ring-1 ring-inset ring-line hover:bg-paper-2',
        ghost: 'bg-transparent text-ink hover:bg-paper-2',
        danger: 'bg-danger text-white hover:brightness-[0.92]',
        success: 'bg-success text-white hover:brightness-[0.92]',
      },
      size: {
        sm: 'h-8 px-3 text-[12.5px]',
        md: 'h-[42px] px-[18px] text-[15px]',
        lg: 'h-[46px] px-5 text-base',
        icon: 'h-9 w-9',
        'icon-sm': 'h-8 w-8 rounded-control',
        'icon-lg': 'h-[42px] w-[42px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  /** Text shown while `loading`; the caller translates it. */
  loadingLabel?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading,
      loadingLabel = 'Lädt …',
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        data-size={size ?? 'md'}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span>{loadingLabel}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
